import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bot, FileText, History, Terminal, Settings as SettingsIcon } from 'lucide-react';
import RulesTab from './tabs/RulesTab';
import TemplatesTab from './tabs/TemplatesTab';
import TriggerHistoryTab from './tabs/TriggerHistoryTab';
import LogsTab from './tabs/LogsTab';
import SettingsTab from './tabs/SettingsTab';

const TABS = [
  { id: 'rules', label: 'Rules', icon: Bot, testid: 'chatbot-tab-rules' },
  { id: 'templates', label: 'Templates', icon: FileText, testid: 'chatbot-tab-templates' },
  { id: 'history', label: 'Trigger History', icon: History, testid: 'chatbot-tab-history' },
  { id: 'logs', label: 'Logs', icon: Terminal, testid: 'chatbot-tab-logs' },
  { id: 'settings', label: 'Settings', icon: SettingsIcon, testid: 'chatbot-tab-settings' },
];

const ChatbotHub = () => {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') || 'rules';
  const activeTab = useMemo(
    () => (TABS.find(t => t.id === rawTab) ? rawTab : 'rules'),
    [rawTab]
  );

  useEffect(() => {
    if (rawTab !== activeTab) {
      const next = new URLSearchParams(params);
      next.set('tab', activeTab);
      setParams(next, { replace: true });
    }
  }, [rawTab, activeTab, params, setParams]);

  const handleTabChange = (id) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: false });
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'rules':
        return <RulesTab />;
      case 'templates':
        return <TemplatesTab />;
      case 'history':
        return <TriggerHistoryTab />;
      case 'logs':
        return <LogsTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <RulesTab />;
    }
  };

  return (
    <div className="space-y-6" data-testid="chatbot-hub">
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
            <Bot size={22} className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Chatbot Center</h1>
            <p className="text-sm text-gray-500">
              Unified automation hub — rules, templates, history, logs & settings.
            </p>
          </div>
        </div>

        <div className="mt-5 border-b border-gray-200 -mb-4 flex flex-wrap gap-1">
          {TABS.map(({ id, label, icon: Icon, testid }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                data-testid={testid}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  isActive
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>{renderTab()}</div>
    </div>
  );
};

export default ChatbotHub;
